// /src/pages/Auth/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../hooks/useAuth";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const GIF_URL = "https://files2.edgagement.com/sites/wb/images/login.gif";

// Account type constants
const USERS_COLLECTION = "users"; // change if our profiles live elsewhere
const ACCOUNT_TYPE_FIELD_CANDIDATES = [
  // "accountType",
  // "role",
  // "type",
  // "level",
  // "roles",
  "plan",
]; // "roles" can be an array
const ALLOWED_ACCOUNT_TYPES = [
  "New Agent",
  "Agent",
  "Leader",
  "Broker",
  "Admin",
];
const DEFAULT_ACCOUNT_TYPE = "New Agent"; // what to use if none found
const LS_KEYS = {
  plan: "wb.plan",
  userId: "wb.userId",
  userEmail: "wb.userEmail",
  userName: "wb.userName",
};

// Helper to normalize account type
const normalizeAccountType = (raw) => {
  if (!raw) return DEFAULT_ACCOUNT_TYPE;

  // Handle arrays (take first valid role)
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const normalized = normalizeAccountType(item);
      if (normalized !== DEFAULT_ACCOUNT_TYPE) {
        return normalized;
      }
    }
    return DEFAULT_ACCOUNT_TYPE;
  }

  const str = String(raw).toLowerCase().trim();

  // Map synonyms to canonical types (return capitalized versions)
  if (["new_agent", "newagent", "trainee", "new", "recruit", "new agent"].includes(str)) {
    return "New Agent";
  }

  if (["mgr", "manager", "lead", "director", "leader"].includes(str)) {
    return "Leader";
  }

  if (["senior broker"].includes(str)) {
    return "Senior Broker";
  }

  if (["broker", "brokerage", "principal"].includes(str)) {
    return "Broker";
  }

  if (["admin", "administrator", "superadmin", "super"].includes(str)) {
    return "Admin";
  }

  // Check if it's clearly agent-like
  if (["agent", "sales", "rep", "representative"].includes(str)) {
    return "Agent";
  }

  // If it matches one of our allowed types (case-insensitive), return properly capitalized version
  const matchedType = ALLOWED_ACCOUNT_TYPES.find(
    type => type.toLowerCase() === str
  );
  if (matchedType) {
    return matchedType;
  }

  return DEFAULT_ACCOUNT_TYPE;
};

// Fetch or initialize account type and user data from Firestore
const fetchOrInitAccountType = async (uid, email) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userSnap = await getDoc(userRef);

    let plan = DEFAULT_ACCOUNT_TYPE;
    let userData = {};

    if (userSnap.exists()) {
      userData = userSnap.data();

      // Check all candidate fields for account type
      for (const field of ACCOUNT_TYPE_FIELD_CANDIDATES) {
        if (userData[field] !== undefined) {
          const normalized = normalizeAccountType(userData[field]);
          if (normalized !== DEFAULT_ACCOUNT_TYPE) {
            plan = normalized;
            break;
          }
        }
      }
    }

    // If no valid account type found or document doesn't exist, create/update the document
    if (plan === DEFAULT_ACCOUNT_TYPE || !userSnap.exists()) {
      await setDoc(
        userRef,
        {
          email,
          plan: DEFAULT_ACCOUNT_TYPE,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(userSnap.exists()
            ? {}
            : {
                // Only set initial fields for new docs
                createdBy: "system",
                lastLogin: serverTimestamp(),
              }),
        },
        { merge: true }
      );
    } else {
      // Update last login time
      await setDoc(
        userRef,
        {
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // Return both account type and user data
    return { plan, userData };
  } catch (error) {
    console.error("Error fetching/initializing account type:", error);
    return { plan: DEFAULT_ACCOUNT_TYPE, userData: {} };
  }
};

// Persist user data to localStorage
const persistUserData = (uid, email, plan, user = null, userData = {}) => {
  localStorage.setItem(LS_KEYS.plan, plan);
  localStorage.setItem(LS_KEYS.userId, uid);
  localStorage.setItem(LS_KEYS.userEmail, email); 
  localStorage.setItem(LS_KEYS.userName, userData.name || userData.fullName || user.displayName);
  // Also persist the full user object for header and other components
  if (user) {
    localStorage.setItem("authUser", JSON.stringify({
      uid: user.uid,
      email: user.email,
      // Use name from Firestore if available, otherwise use displayName or email
      name: userData.name || userData.fullName || user.displayName || email.split('@')[0],
      displayName: userData.name || userData.fullName || user.displayName || email.split('@')[0],
      fullName: userData.fullName || userData.name || user.displayName || email.split('@')[0],
      photoURL: user.photoURL || userData.photoUrl || null,
    }));
  }
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const [gifLoaded, setGifLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state && location.state.from) || "/";

  // comes from AuthProvider (loads Firestore profile by email + admin flag)
  const { user, loading: authLoading } = useAuth();

  // Hydrate localStorage if session exists but LS is missing account type
  useEffect(() => {
    const hydrateAccountType = async () => {
      if (user && !localStorage.getItem(LS_KEYS.plan)) {
        try {
          const { plan, userData } = await fetchOrInitAccountType(
            user.uid,
            user.email
          );
          persistUserData(user.uid, user.email, plan, user, userData);
        } catch (error) {
          console.error("Error hydrating account type:", error);
        }
      }
    };

    hydrateAccountType();
  }, [user]);

  // Already signed in? Redirect to intended route (no longer admin-only)
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [authLoading, user, from, navigate]);

  useEffect(() => {
    const i = new Image();
    i.onload = () => setGifLoaded(true);
    i.src = GIF_URL;
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setResetMsg("");
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        pass
      );
      const user = userCredential.user;

      // Fetch or initialize account type and user data from Firestore
      const { plan, userData } = await fetchOrInitAccountType(user.uid, user.email);

      // Persist to localStorage (including the full user object and Firestore data)
      persistUserData(user.uid, user.email, plan, user, userData);

      // Show non-blocking notice if account type is not recognized
      if (plan === DEFAULT_ACCOUNT_TYPE) {
        console.log("Account type set to default:", DEFAULT_ACCOUNT_TYPE);
      }

      // Redirect will happen automatically via the useEffect above
    } catch (error) {
      console.error(error);
      const msg =
        error.code === "auth/user-not-found"
          ? "No account found for that email."
          : error.code === "auth/wrong-password"
          ? "Incorrect password."
          : error.code === "auth/too-many-requests"
          ? "Too many attempts. Try again later."
          : error.code === "auth/invalid-email"
          ? "Please enter a valid email."
          : "Unable to sign in.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setErr("");
    setResetMsg("");
    if (!email) {
      setErr("Enter your email first, then click Forgot password.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg("Password reset email sent.");
    } catch (error) {
      console.error(error);
      const msg =
        error.code === "auth/user-not-found"
          ? "No account found for that email."
          : "Could not send reset email.";
      setErr(msg);
    }
  }

  return (
    <div style={styles.page}>
      {/* LEFT: GIF */}
      <section style={styles.left}>
        {!gifLoaded && <div style={styles.skeleton} />}
        <img
          src={GIF_URL}
          alt="Wealth Builders motion graphic"
          style={{ ...styles.gif, opacity: gifLoaded ? 1 : 0 }}
        />
        <div style={styles.overlayBrand}>
          <span style={styles.brandMark}>Wealth Builders</span>
        </div>
      </section>

      {/* RIGHT: Login card */}
      <section style={styles.right}>
        <div style={styles.card}>
          <h2 style={styles.title}>Sign in</h2>

          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={styles.input}
                autoComplete="email"
              />
            </label>

            <label style={styles.label}>
              Password
              <div style={styles.inputWrap}>
                <input
                  type={show ? "text" : "password"}
                  required
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="••••••••"
                  style={{ ...styles.input, paddingRight: 64 }} // room for Show btn
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  style={styles.showBtn}
                >
                  {show ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            {err && (
              <div
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "rgba(255,0,0,0.1)",
                  border: "1px solid rgba(255,0,0,0.3)",
                  color: "#ff6b6b",
                  fontSize: 13,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {err}
              </div>
            )}

            {resetMsg && (
              <div
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "rgba(0,128,0,0.12)",
                  border: "1px solid rgba(0,255,0,0.25)",
                  color: "#6DFF8B",
                  fontSize: 13,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {resetMsg}
              </div>
            )}

            <button type="submit" style={styles.submit} disabled={loading}>
              {loading ? "Signing in…" : "Continue"}
            </button>
          </form>

          <div style={styles.footerRow}>
            <Link to="#" onClick={handleReset} style={styles.link}>
              Forgot password?
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* =========================
   Inline styles
   ========================= */
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    background: "#000",
    color: "#fff",
  },
  left: {
    flex: 1,
    display: "grid",
    placeItems: "center",
    position: "relative",
  },
  gif: {
    maxWidth: 400,
    maxHeight: 400,
    objectFit: "contain",
    transition: "opacity 300ms ease",
  },
  skeleton: {
    width: 300,
    height: 300,
    background: "rgba(255,215,0,0.05)",
    borderRadius: "50%",
  },
  overlayBrand: { position: "absolute", bottom: 24, left: 24 },
  brandMark: {
    fontWeight: 700,
    letterSpacing: 0.4,
    color: "#f5d66a",
    background: "rgba(0,0,0,0.5)",
    padding: "6px 10px",
    borderRadius: 8,
  },

  right: { flex: 1, display: "grid", placeItems: "center", padding: 24 },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "#111",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
    display: "grid",
    alignContent: "start",
    gap: 10,
  },
  title: { margin: "4px 0 6px", fontSize: 24, fontWeight: 700 },
  form: { display: "grid", gap: 14 },
  label: { display: "grid", gap: 6, fontSize: 14, color: "#e7e7ea" },
  inputWrap: { position: "relative" },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    padding: "12px 14px",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  showBtn: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#cfcfd6",
    cursor: "pointer",
    padding: "6px 8px",
    borderRadius: 8,
  },
  submit: {
    marginTop: 2,
    background:
      "linear-gradient(135deg, rgba(255,215,0,0.9), rgba(255,215,0,0.75))",
    color: "#000",
    fontWeight: 700,
    border: "none",
    borderRadius: 12,
    padding: "12px 14px",
    cursor: "pointer",
  },
  footerRow: {
    marginTop: 6,
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  link: { color: "#f5d66a", textDecoration: "none", fontSize: 13 },
};
